import { motion, AnimatePresence } from "framer-motion";
import { Plus, ShieldCheck, Smartphone, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import { useAccounts, type Account } from "@/api/accounts";
import { AccountCard } from "@/components/AccountCard";
import { AccountEditSheet } from "@/components/AccountEditSheet";
import { AddAccountSheet } from "@/components/AddAccountSheet";

export function AccountsPage() {
  const { data: accounts, isLoading } = useAccounts();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);

  const list = accounts ?? [];
  const onlineCount = list.filter((a) => a.health === "online").length;
  const limitedCount = list.filter((a) => a.health === "limited").length;
  const bannedCount = list.filter((a) => a.health === "banned" || !a.is_enabled).length;

  return (
    <div className="px-5 pt-4 pb-safe-nav">
      {/* Header */}
      <div className="flex items-center justify-between pt-2 pb-5">
        <div>
          <div className="text-2xl font-semibold tracking-tight">Аккаунты</div>
          <div className="text-sm text-text-muted mt-0.5">
            {list.length === 0
              ? "Подключите первый аккаунт"
              : `${list.length} ${pluralRu(list.length, "аккаунт", "аккаунта", "аккаунтов")}`}
          </div>
        </div>

        {list.length > 0 && (
          <button
            onClick={() => {
              haptic.impact("medium");
              setSheetOpen(true);
            }}
            className={cn(
              "size-11 rounded-full grid place-items-center",
              "bg-gradient-accent text-white",
              "shadow-[0_8px_24px_-8px_rgba(255,69,0,0.6)]",
              "active:scale-95 transition-transform",
            )}
            aria-label="Добавить аккаунт"
          >
            <Plus className="size-5" />
          </button>
        )}
      </div>

      {/* Health summary — only when there are accounts */}
      {list.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-3 gap-2.5 mb-5"
        >
          <HealthStat
            value={onlineCount}
            label="в сети"
            tone="success"
          />
          <HealthStat
            value={limitedCount}
            label="лимит"
            tone="warning"
          />
          <HealthStat
            value={bannedCount}
            label="недоступно"
            tone="danger"
          />
        </motion.div>
      )}

      {/* Content */}
      <AnimatePresence mode="popLayout">
        {isLoading ? (
          <SkeletonList key="loading" />
        ) : list.length === 0 ? (
          <EmptyState key="empty" onAdd={() => setSheetOpen(true)} />
        ) : (
          <motion.div key="list" layout className="space-y-2.5">
            {list.map((a) => (
              <AccountCard
                key={a.id}
                account={a}
                onMenu={setEditing}
                onClick={setEditing}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <AddAccountSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onSuccess={(a: Account) => {
          toast.success(`${a.name} подключён`, { description: a.phone });
        }}
      />
      <AccountEditSheet
        open={!!editing}
        account={editing}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

// ============================================================
// Empty state — premium illustration with CTA
// ============================================================

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative pt-6 pb-8"
    >
      {/* Decorative glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-10 w-[80%] aspect-square rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(255,107,53,0.35) 0%, rgba(255,69,0,0.12) 38%, rgba(0,0,0,0) 70%)",
          filter: "blur(30px)",
        }}
      />

      <div className="relative flex flex-col items-center text-center px-6 space-y-5">
        {/* Illustration: stacked phone + sparkle ring */}
        <div className="relative size-32">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 rounded-full border border-dashed border-accent/30"
          />
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: [0.95, 1.04, 0.95] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-4 rounded-full border border-accent/15"
          />
          <div className="absolute inset-8 rounded-[24px] bg-gradient-accent grid place-items-center shadow-[0_20px_60px_-12px_rgba(255,69,0,0.6)]">
            <Smartphone className="size-9 text-white" strokeWidth={2.2} />
          </div>
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-1 -right-1 size-7 rounded-xl bg-surface border border-white/[0.08] grid place-items-center"
          >
            <Sparkles className="size-3.5 text-accent" />
          </motion.div>
        </div>

        <div className="space-y-2 max-w-[300px]">
          <div className="text-[22px] font-semibold tracking-tight leading-tight">
            Подключите первый
            <br />
            <span className="text-gradient-accent">Telegram-аккаунт</span>
          </div>
          <div className="text-sm text-text-muted leading-relaxed">
            С&nbsp;него будут уходить публикации. Можно подключить несколько — мы автоматически
            распределим нагрузку.
          </div>
        </div>

        <Button size="lg" leftIcon={<Plus />} onClick={onAdd} className="mt-2 px-8">
          Добавить аккаунт
        </Button>

        {/* Reassurance row */}
        <div className="flex items-center gap-2 text-[11px] text-text-dim mt-3">
          <ShieldCheck className="size-3.5" />
          <span>Сессии шифруются. Мы не читаем сообщения.</span>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================
// Health summary stat
// ============================================================

function HealthStat({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone: "success" | "warning" | "danger";
}) {
  const dot =
    tone === "success"
      ? "bg-success shadow-[0_0_8px_rgba(74,222,128,0.6)]"
      : tone === "warning"
        ? "bg-warning shadow-[0_0_8px_rgba(251,191,36,0.5)]"
        : "bg-danger shadow-[0_0_8px_rgba(255,77,109,0.5)]";

  return (
    <div className="relative rounded-2xl p-3 bg-white/[0.025] border border-white/[0.06]">
      <div className="flex items-center gap-1.5">
        <span className={cn("size-1.5 rounded-full", dot)} />
        <span className="text-[10px] text-text-dim uppercase tracking-wider font-medium">
          {label}
        </span>
      </div>
      <div className="text-[22px] font-semibold tabular-nums tracking-tight mt-1">{value}</div>
    </div>
  );
}

// ============================================================
// Skeleton placeholder while loading
// ============================================================

function SkeletonList() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-2.5"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-[88px] rounded-2xl bg-white/[0.025] border border-white/[0.06] relative overflow-hidden"
        >
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
        </div>
      ))}
    </motion.div>
  );
}

// ============================================================
// Utils
// ============================================================

function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
